import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ScorecardNavigation from '../ScorecardNavigation';
import FullPageSpinner from '../../FullPageSpinner';
import * as Sentry from '@sentry/react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import {
  WINDOW_OPTIONS,
  DEFAULT_WINDOW,
  WindowDays,
  StatRow,
  JudgeStatRow,
  WorklistRow,
  LocationOption,
  EvaluatorStatRow,
  EvaluatorStatsResponse,
  num,
  pct,
  judgePct,
  prettyType,
  isReviewType,
  REJECTION_REVIEW_TYPES,
} from './interactionEvaluationsQueries';
import {
  getInteractionEvalStats,
  getInteractionEvalJudgeStats,
  getInteractionEvalWorklist,
  getInteractionEvalLocations,
  getInteractionEvalEvaluatorStats,
} from '../../../services/scopedReadsService';
import { useAuth } from '../../../contexts/AuthContext';

// feat-130 — Shift-Interaction Evaluation Dashboard (internal admin, read-only).
// feat-133 Batch-5: the two aggregate reads + the location dropdown now go through
// purpose-built, admin-gated (require_admin), %s-parameterized endpoints
// (/api/admin/interaction-evaluations/*) instead of the raw /admin/execute-select
// passthrough — `days` is validated to {7,14,30} and `location_id` to a UUID
// server-side (the client-side UUID guard is retired). Cross-tenant BY DESIGN
// (an admin eval tool, DP-5a). Pure formatting helpers live in
// ./interactionEvaluationsQueries.

// DB timestamps come back without a zone; treat as UTC (append 'Z') and render Pacific,
// matching RecentShiftsTable / ServerSummaryView.
const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  try {
    const utc = value.endsWith('Z') ? value : `${value}Z`;
    return formatInTimeZone(parseISO(utc), 'America/Los_Angeles', 'MMM d, yyyy h:mm a');
  } catch {
    return value;
  }
};

const formatDuration = (created: string | null, ended: string | null): string => {
  if (!created || !ended) return '—';
  try {
    const start = parseISO(created.endsWith('Z') ? created : `${created}Z`).getTime();
    const end = parseISO(ended.endsWith('Z') ? ended : `${ended}Z`).getTime();
    const mins = Math.round((end - start) / 60000);
    if (!Number.isFinite(mins) || mins < 0) return '—';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  } catch {
    return '—';
  }
};

/**
 * feat-191 Part E — the agreement cell. ONE component with mutually exclusive
 * shapes and no third path, so the percentage and its denominator are emitted by
 * the SAME branch and no later cleanup can separate them.
 *
 * Why that structure rather than a muted sub-line: a "over 12 judged" caption next
 * to a bold percentage reads as redundancy, and redundancy is what a cleanup
 * deletes. Here there is no branch that renders a bare percentage to delete down to.
 *
 * `pct` is null for TWO different reasons — a zero denominator, and deliberate
 * suppression below `min` — and the two get different copy, because "no judged
 * grades yet" and "not enough judged grades yet" are different facts about a
 * contractor. Neither one re-derives the number: `matches / denominator` is never
 * computed here. The backend withheld the percentage on purpose.
 */
const AgreementCell: React.FC<{
  pct: number | null;
  denominator: number;
  min: number;
}> = ({ pct: value, denominator, min }) => {
  if (denominator === 0) {
    return (
      <span className="text-inkMuted">
        — <span className="text-xs">(no judged grades)</span>
      </span>
    );
  }
  if (value == null) {
    return (
      <span className="text-inkMuted">
        — <span className="text-xs">(n={denominator}, needs {min})</span>
      </span>
    );
  }
  return (
    <span className="text-ink font-medium">
      {Math.round(value)}% <span className="text-xs font-normal text-inkMuted">(n={denominator})</span>
    </span>
  );
};

const StatCard: React.FC<{ label: string; value: string; sub?: string; accent?: string }> = ({
  label,
  value,
  sub,
  accent = 'text-ink',
}) => (
  <div className="bg-white border border-softBorder rounded-xl p-4 shadow-sm">
    <div className="text-xs font-medium uppercase tracking-wide text-inkMuted">{label}</div>
    <div className={`mt-1 text-2xl font-display font-semibold ${accent}`}>{value}</div>
    {sub && <div className="text-xs text-inkMuted mt-0.5">{sub}</div>}
  </div>
);

const InteractionEvaluations: React.FC = () => {
  const [windowDays, setWindowDays] = useState<WindowDays>(DEFAULT_WINDOW);
  const [locationId, setLocationId] = useState<string>('');
  // feat-196 (B1 / D6) — "Live clients only", DEFAULT ON. Local state, matching
  // `windowDays`/`locationId` above: this page is not URL-backed, and the drill-down
  // links serialize the selectors into the child routes (which ARE URL-backed). That
  // one-way mechanism is what `days` already uses and what AC10 requires the toggle
  // to reuse — a second mechanism here would be the thing that drifts.
  //
  // Consequence, inherited rather than introduced: navigating BACK from a drill-down
  // resets this to ON, exactly as it already resets `days` to 14. Tracked as FU-1
  // (the real fix is making this page URL-backed like its two children), explicitly
  // out of scope per D9.
  const [liveOnly, setLiveOnly] = useState<boolean>(true);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [stats, setStats] = useState<StatRow[]>([]);
  // feat-156 — offline/live judge stats, fetched from the separate judge-stats
  // endpoint and merged onto `stats` by interaction_type. Loaded NON-FATALLY so
  // the human-verification table still renders if the endpoint isn't live yet.
  const [judgeStats, setJudgeStats] = useState<JudgeStatRow[]>([]);
  const [worklist, setWorklist] = useState<WorklistRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // feat-191 Part E — admin-only per-grader panel. Null until fetched, and null
  // FOREVER for a non-admin: the fetch is never issued for them and the panel is
  // absent, not greyed and not "restricted".
  const [evaluatorStats, setEvaluatorStats] = useState<EvaluatorStatsResponse | null>(null);
  const [evaluatorStatsError, setEvaluatorStatsError] = useState<string | null>(null);

  // feat-191 — this page is <EvaluatorRoute>-gated, so it now renders for Admin OR
  // Evaluator. A local, normalized presentation memo, mirroring the ones in
  // ScorecardNavigation. NOT createPermissionChecker: that file is an allowlist
  // which correctly denies an unknown role all 12 capabilities and must not learn
  // the word "evaluator" (C6).
  const { user } = useAuth();
  const isAdmin = useMemo(() => {
    const raw = user?.managementUser?.role;
    if (!raw) return false;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() === 'Admin';
  }, [user?.managementUser?.role]);

  // Location options for the drill-in filter.
  // feat-196 (D3) — no longer "loaded once": the list is scoped by `liveOnly`, so
  // the dropdown stops OFFERING a non-live client's location rather than offering
  // one that yields a guaranteed-empty table. That makes `liveOnly` a dependency.
  //
  // If the currently-selected location disappears from the list when the toggle
  // goes ON, the selection is cleared back to "All locations" — otherwise the page
  // would keep filtering by a location the dropdown no longer shows, which reads as
  // "the filter is broken" and is unrecoverable without a reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = (await getInteractionEvalLocations({ liveOnly })) as LocationOption[];
        if (cancelled) return;
        setLocations(rows);
        setLocationId((current) =>
          current && !rows.some((l) => l.id === current) ? '' : current,
        );
      } catch (e) {
        // Non-fatal: the filter just stays "All locations".
        Sentry.captureException(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [liveOnly]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setEvaluatorStatsError(null);
    try {
      // feat-133 Batch-5: admin-gated scoped endpoints. locationId '' (All
      // locations) → omit the param; days is the {7,14,30} enum. Server-side
      // validation rejects out-of-enum days / non-UUID location.
      //
      // feat-156: the judge-stats overlay is fetched concurrently but its failure
      // must NOT break the human-verification table (the endpoint may not be
      // deployed yet, pre-mig100). So it gets its own .catch → [] and is awaited
      // separately from the fatal stats/worklist pair.
      const judgeStatsPromise = getInteractionEvalJudgeStats({
        days: windowDays,
        locationId: locationId || undefined,
        liveOnly,
      }).catch((e) => {
        // Non-fatal: the two judge columns just render '—'.
        Sentry.captureException(e);
        return [] as JudgeStatRow[];
      });
      // feat-191 Part E — same non-fatal treatment as judge-stats, and for a
      // stronger reason: this is the newest endpoint on the page, so it is the one
      // most likely to 404 during a partial rollout. Not fetched at all for a
      // non-admin — an evaluator's token 403s on it by design (AC13), and firing a
      // request we know will fail would put an error on the one page this role exists
      // to use. The 403 is the boundary; not fetching is hygiene.
      const evaluatorStatsPromise = isAdmin
        ? getInteractionEvalEvaluatorStats({
            days: windowDays,
            locationId: locationId || undefined,
            liveOnly,
          }).catch((e) => {
            Sentry.captureException(e);
            setEvaluatorStatsError(e instanceof Error ? e.message : 'Failed to load');
            return null;
          })
        : Promise.resolve(null);
      const [statRows, worklistRows] = await Promise.all([
        getInteractionEvalStats({ days: windowDays, locationId: locationId || undefined, liveOnly }),
        getInteractionEvalWorklist({ days: windowDays, locationId: locationId || undefined, liveOnly }),
      ]);
      setStats(statRows as StatRow[]);
      setWorklist(worklistRows as WorklistRow[]);
      setJudgeStats((await judgeStatsPromise) as JudgeStatRow[]);
      setEvaluatorStats((await evaluatorStatsPromise) as EvaluatorStatsResponse | null);
    } catch (e) {
      console.error('Failed to load interaction evaluations:', e);
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [windowDays, locationId, isAdmin, liveOnly]);

  // Re-query both sections whenever the window or location filter changes.
  useEffect(() => {
    load();
  }, [load]);

  // Totals are summed client-side from the per-type breakdown so both views share one query.
  const totals = useMemo(() => {
    return stats.reduce(
      (acc, r) => ({
        total: acc.total + num(r.total),
        verified: acc.verified + num(r.verified),
        correct: acc.correct + num(r.correct),
        incorrect: acc.incorrect + num(r.incorrect),
        unverified: acc.unverified + num(r.unverified),
      }),
      { total: 0, verified: 0, correct: 0, incorrect: 0, unverified: 0 },
    );
  }, [stats]);

  const labeled = totals.correct + totals.incorrect;

  // feat-156 — index the judge-stats overlay by interaction_type for O(1) lookup
  // per row. Types the offline judge doesn't cover simply aren't in the map → the
  // two columns render '—'.
  const judgeByType = useMemo(() => {
    const m = new Map<string, JudgeStatRow>();
    for (const j of judgeStats) {
      if (j.interaction_type) m.set(j.interaction_type, j);
    }
    return m;
  }, [judgeStats]);

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-4xl font-display font-semibold text-ink mb-1">
              Shift Interaction Evaluations
            </h1>
            <p className="text-sm text-inkMuted">
              Read-only QA view of how many AI-detected interactions have been human-verified, and
              how often the AI was judged correct — across all tenants over the recent window.
            </p>
          </div>
          {/* feat-160 — entry into the standalone cross-type disagreements view.
              feat-196 (AC10): this link carries the toggle state. It is the ONE
              affordance that had no query params at all, so unlike the per-type link
              below there was nothing to append to — param passing is ADDED here.
              Only `live_only` is carried: the pre-existing fact that this link also
              drops `days`/`location_id` is FU-1 and deliberately NOT fixed inside
              this feature (product-lead ruling; silently repairing an unrelated bug
              inside a scoped diff is what makes it unreviewable). */}
          <Link
            to={`/restaurant/internal/interaction-evaluations/disagreements${
              liveOnly ? '' : '?live_only=false'
            }`}
            className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium border border-softBorder bg-white text-coral hover:bg-cream whitespace-nowrap"
          >
            View disagreements →
          </Link>
          {/* feat-199 — the recall-QA queue: candidates the live double-check THREW
              AWAY. This is the only entry point to that surface; without it the queue
              is reachable solely by typing a URL, which fails AC1 ("an evaluator
              selects an interaction type and reviews discarded candidates") for the
              contractor it was built for. Defaults to the first reviewable type;
              the queue's own picker moves between them. */}
          <Link
            to={`/restaurant/internal/interaction-evaluations/rejections/${REJECTION_REVIEW_TYPES[0]}`}
            data-testid="recall-queue-link"
            className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium border border-softBorder bg-white text-coral hover:bg-cream whitespace-nowrap"
          >
            Review discarded →
          </Link>
          {/* ADMIN-ONLY, matching the route guard and the backend gate
              (`recall-metrics` is `require_admin`). Hidden for an evaluator on the
              ANCHORING ground that decided §6.0: Shane PRODUCES the overturn rate, and
              a reviewer who has seen it has a number to calibrate toward. The hidden
              link is not the boundary — the 403 is — but offering a link that 403s is
              its own defect. */}
          {isAdmin && (
            <Link
              to="/restaurant/internal/interaction-evaluations/recall-metrics"
              data-testid="recall-metrics-link"
              className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium border border-softBorder bg-white text-coral hover:bg-cream whitespace-nowrap"
            >
              Recall metrics →
            </Link>
          )}
        </div>

        {/* Controls: recent-window toggle + optional location filter */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink">Window:</span>
            <div className="inline-flex rounded-lg border border-softBorder bg-white overflow-hidden">
              {WINDOW_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    windowDays === d
                      ? 'bg-coral text-white'
                      : 'text-inkMuted hover:bg-cream'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="locationFilter" className="text-sm font-medium text-ink">
              Location:
            </label>
            <select
              id="locationFilter"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="px-3 py-1.5 border border-softBorder rounded-md bg-white text-sm"
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          {/* feat-196 (B1 / D6) — "Live clients only", default ON. Sits with the
              other selectors because it is one: a population filter in the same row
              as Window and Location, matching this page's existing conventions.
              D9 forbids a filter badge or an "N hidden" counter, so the LABEL is the
              entire explanation the user gets for what the toggle does. */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="live-only-toggle"
                checked={liveOnly}
                onChange={(e) => setLiveOnly(e.target.checked)}
                className="h-4 w-4 rounded border-softBorder text-coral focus:ring-coral"
              />
              <span className="text-sm font-medium text-ink">Live clients only</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Error loading data: {error}
          </div>
        )}

        {isLoading ? (
          <FullPageSpinner />
        ) : (
          <>
            {/* Section A — Verification stats */}
            <section className="mb-10">
              <h2 className="text-lg font-display font-semibold text-ink mb-3">
                Verification stats
                <span className="ml-2 text-sm font-normal text-inkMuted">
                  {windowDays === 1 ? 'yesterday + today' : `last ${windowDays} days`}
                </span>
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <StatCard label="Interactions" value={totals.total.toLocaleString()} />
                <StatCard
                  label="Verified"
                  value={totals.verified.toLocaleString()}
                  sub={`${pct(totals.verified, totals.total)} of total`}
                  accent="text-blue-600"
                />
                <StatCard label="Correct" value={totals.correct.toLocaleString()} accent="text-green-600" />
                <StatCard label="Incorrect" value={totals.incorrect.toLocaleString()} accent="text-red-600" />
                <StatCard label="Unverified" value={totals.unverified.toLocaleString()} accent="text-inkMuted" />
                <StatCard
                  label="AI accuracy"
                  value={pct(totals.correct, labeled)}
                  sub="of labeled only"
                  accent="text-coral"
                />
              </div>

              <p className="text-xs text-inkMuted mb-4">
                AI accuracy is correct / (correct + incorrect) — computed over human-labeled
                interactions only ({labeled.toLocaleString()} labeled), not the full population.
                <br />
                <span className="font-medium">Discarded %</span> is the live double-check's reject rate
                (rejected / confirmed + rejected); <span className="font-medium">Judge agreement %</span> is
                the independent offline judge's confirm rate (confirmed / judged), covering greeting and
                upsell only. <span className="font-medium">Uncertain</span> counts windows the judge flagged
                as too insufficient to grade — routed to human review, excluded from Judge agreement %. All
                show <span className="font-mono">—</span> until judgements have been recorded.
              </p>

              {/* Per-interaction_type breakdown */}
              <div className="bg-white shadow-sm border border-softBorder rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-softBorder">
                    <thead className="bg-cream/60">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Interaction type</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Total</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Verified</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">% verified</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Correct</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Incorrect</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Unverified</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">AI accuracy</th>
                        {/* feat-156 — Interaction Judge validation columns (offline judge covers
                            greeting + upsell only; '—' elsewhere / until judged). */}
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider" title="Live double-check: rejected / (confirmed + rejected)">Discarded %</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider" title="Offline judge: confirmed / judged (greeting + upsell only)">Judge agreement %</th>
                        {/* feat-174 — offline judge `uncertain` count: windows too
                            insufficient to grade, routed to human review. Excluded from
                            Judge agreement % (neither agreement nor disagreement). */}
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider" title="Offline judge: uncertain count — routed to human review (excluded from Judge agreement %)">Uncertain</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-softBorder">
                      {stats.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-4 py-8 text-center text-sm text-inkMuted">
                            No interactions in this window.
                          </td>
                        </tr>
                      ) : (
                        stats.map((r) => {
                          const total = num(r.total);
                          const verified = num(r.verified);
                          const correct = num(r.correct);
                          const incorrect = num(r.incorrect);
                          const rowLabeled = correct + incorrect;
                          const judge = r.interaction_type ? judgeByType.get(r.interaction_type) : undefined;
                          // feat-159 — the 3 in-scope types drill into the per-type
                          // review view, carrying the current Window + Location. Other
                          // types stay non-interactive (review not available yet).
                          const reviewable = isReviewType(r.interaction_type);
                          // feat-196 (AC10) — the toggle rides along with Window +
                          // Location. Appended in the canonical default-ON-by-absence
                          // form: `live_only=false` only when OFF, never
                          // `live_only=true` (ADR 0001 §4.3), so a link built with the
                          // toggle ON is byte-identical to the pre-feature link.
                          const reviewHref = reviewable
                            ? `/restaurant/internal/interaction-evaluations/review/${r.interaction_type}?days=${windowDays}${
                                locationId ? `&location_id=${encodeURIComponent(locationId)}` : ''
                              }${liveOnly ? '' : '&live_only=false'}`
                            : null;
                          return (
                            <tr key={r.interaction_type ?? 'unknown'} className="hover:bg-cream/40">
                              <td className="px-4 py-2.5 text-sm font-medium text-ink">
                                {reviewHref ? (
                                  <Link
                                    to={reviewHref}
                                    className="text-coral hover:underline font-medium"
                                    title="Review this type one interaction at a time"
                                  >
                                    {prettyType(r.interaction_type)} →
                                  </Link>
                                ) : (
                                  <span
                                    className="text-ink"
                                    title="Per-type review not available for this interaction type yet"
                                  >
                                    {prettyType(r.interaction_type)}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-sm text-right text-ink">{total.toLocaleString()}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-ink">{verified.toLocaleString()}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-inkMuted">{pct(verified, total)}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-green-600">{correct.toLocaleString()}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-red-600">{incorrect.toLocaleString()}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-inkMuted">{num(r.unverified).toLocaleString()}</td>
                              <td className="px-4 py-2.5 text-sm text-right font-medium text-coral">{pct(correct, rowLabeled)}</td>
                              {/* feat-156 — backend-computed %s, rendered verbatim (never recomputed here). */}
                              <td className="px-4 py-2.5 text-sm text-right text-inkMuted">{judgePct(judge?.discarded_pct)}</td>
                              <td className="px-4 py-2.5 text-sm text-right text-inkMuted">{judgePct(judge?.judge_agreement_pct)}</td>
                              {/* feat-174 — raw uncertain count (verbatim; '—' when absent / type out of judge scope). */}
                              <td className="px-4 py-2.5 text-sm text-right text-indigo-600">
                                {judge?.offline_uncertain == null ? '—' : num(judge.offline_uncertain).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Section B — Unverified-shift worklist */}
            <section>
              <h2 className="text-lg font-display font-semibold text-ink mb-1">
                Unverified-shift worklist
              </h2>
              <p className="text-sm text-inkMuted mb-3">
                Recent shifts with at least one interaction and <span className="font-medium">zero</span>{' '}
                human-verified interactions — most recent first. Open one to start reviewing.
              </p>

              <div className="bg-white shadow-sm border border-softBorder rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-softBorder">
                    <thead className="bg-cream/60">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Shift date</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Server</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Location</th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Interactions</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Duration</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-softBorder">
                      {worklist.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-sm text-inkMuted">
                            No unverified shifts in this window — everything recent has been reviewed. 🎉
                          </td>
                        </tr>
                      ) : (
                        worklist.map((w) => (
                          <tr key={w.shift_id} className="hover:bg-cream/40">
                            <td className="px-4 py-2.5 text-sm text-ink whitespace-nowrap">{formatDateTime(w.shift_created)}</td>
                            <td className="px-4 py-2.5 text-sm text-ink">{w.server_name || '—'}</td>
                            <td className="px-4 py-2.5 text-sm text-inkMuted">{w.location_name || '—'}</td>
                            <td className="px-4 py-2.5 text-sm text-right text-ink">{num(w.interaction_count).toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-sm text-inkMuted whitespace-nowrap">{formatDuration(w.shift_created, w.shift_ended)}</td>
                            <td className="px-4 py-2.5 text-sm whitespace-nowrap">
                              <Link
                                to={`/restaurant/shifts/${w.shift_id}/transcript/${w.server_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-coral hover:underline font-medium"
                              >
                                Review →
                              </Link>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {worklist.length >= 200 && (
                <p className="text-xs text-inkMuted mt-2">
                  Showing the 200 most-recent unverified shifts. Narrow the window or location to see fewer.
                </p>
              )}
            </section>

            {/* feat-191 Part E — Section C: per-grader throughput and agreement.
                ADMIN-ONLY, and ABSENT for an evaluator rather than greyed or
                "restricted": it is oversight OF them and it names other graders by
                email. The real boundary is the endpoint's require_admin (it 403s for
                an evaluator token, AC13) — this condition is what keeps the page from
                rendering a broken card, not what protects the data. */}
            {isAdmin && (
              <section className="mb-10" data-testid="evaluator-stats-panel">
                <h2 className="text-lg font-display font-semibold text-ink mb-1">
                  Grader activity
                  <span className="ml-2 text-xs font-medium uppercase tracking-wide text-inkMuted bg-cream-dark px-2 py-0.5 rounded">
                    Admin only
                  </span>
                </h2>
                {/* The header STATES the window and the location, and that is a
                    requirement rather than decoration: without it "412 graded" reads
                    as a person's total when it is a claim about one location. It also
                    says "graded in", not "last N days", because this panel windows on
                    verified_at (when the grading happened) while every other number on
                    this page windows on s.created (when the interaction happened) —
                    the two will not reconcile, by design. */}
                <p className="text-xs text-inkMuted mb-3">
                  Graded{' '}
                  {windowDays === 1 ? 'yesterday + today' : `in the last ${windowDays} days`} ·{' '}
                  {locationId
                    ? locations.find((l) => l.id === locationId)?.name || 'selected location'
                    : 'all locations'}
                  {evaluatorStats?.window_basis && evaluatorStats.window_basis !== 'verified_at' && (
                    <> · window basis: {evaluatorStats.window_basis}</>
                  )}
                  . Counts when the grading happened, not when the interaction did — so this
                  panel does not reconcile with the stats above, deliberately.
                </p>

                {evaluatorStatsError ? (
                  // Deliberately quieter than the page-level red banner: this is the
                  // newest endpoint on the page and the most likely to 404 mid-rollout,
                  // and its absence must not read as the dashboard being broken.
                  <div className="bg-white border border-softBorder rounded-xl p-4 text-sm text-inkMuted">
                    Grader activity is unavailable right now. The rest of this page is unaffected.
                  </div>
                ) : (
                  <div className="bg-white shadow-sm border border-softBorder rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-softBorder">
                        <thead className="bg-cream/60">
                          <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Grader</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Graded</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Correct</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-inkMuted uppercase tracking-wider">Incorrect</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Agreement w/ judge</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-inkMuted uppercase tracking-wider">Last graded</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-softBorder">
                          {(evaluatorStats?.evaluators ?? []).length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-sm text-inkMuted">
                                Nobody graded an interaction in this window.
                              </td>
                            </tr>
                          ) : (
                            (evaluatorStats?.evaluators ?? []).map((row: EvaluatorStatRow) => (
                              <tr key={row.evaluator} className="hover:bg-cream/40">
                                <td className="px-4 py-2.5 text-sm text-ink">{row.evaluator}</td>
                                <td className="px-4 py-2.5 text-sm text-right text-ink">
                                  {num(row.grades_total).toLocaleString()}
                                </td>
                                <td className="px-4 py-2.5 text-sm text-right text-green-600">
                                  {num(row.grades_correct).toLocaleString()}
                                </td>
                                <td className="px-4 py-2.5 text-sm text-right text-red-600">
                                  {num(row.grades_incorrect).toLocaleString()}
                                </td>
                                <td className="px-4 py-2.5 text-sm whitespace-nowrap">
                                  <AgreementCell
                                    pct={row.agreement_pct}
                                    denominator={num(row.agreement_denominator)}
                                    min={num(evaluatorStats?.min_agreement_denominator)}
                                  />
                                </td>
                                <td className="px-4 py-2.5 text-sm text-inkMuted whitespace-nowrap">
                                  {formatDateTime(row.last_verified_at)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <p className="text-xs text-inkMuted mt-2">
                  <span className="font-medium">Agreement w/ judge</span> is measured only over
                  grades that carry a current offline-judge verdict — the{' '}
                  <span className="font-mono">n</span> beside it — not over everything the
                  person graded. Below{' '}
                  {num(evaluatorStats?.min_agreement_denominator) || 'the minimum'} judged grades
                  the percentage is withheld rather than shown, because a rate over a handful of
                  items says more about the sample than the grader. Everyone who graded appears
                  here, including admins.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InteractionEvaluations;
